import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import { ContentCopyIcon } from "../../icons";
import { useRootStore } from "../../store/contexts";

export const TwoFaSetupForm = observer(() => {
  const rootStore = useRootStore();
  const auth = rootStore.authStore;
  const lang = rootStore.langStore;
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    auth.setupTwoFa().catch(() => {
      setError(lang.serverError || "Error");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only setup; adding auth/lang would re-trigger setupTwoFa on store mutations
  }, []);

  const handleCopyBackupCodes = () => {
    if (auth.twoFaSetupData?.backupCodes) {
      navigator.clipboard.writeText(auth.twoFaSetupData.backupCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleConfirm = async () => {
    setError("");
    try {
      await auth.confirmTwoFaSetup();
    } catch (e: any) {
      setError(lang.twoFaInvalidCode || "Incorrect code, please try again");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    }
  };

  if (!auth.twoFaSetupData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography>{lang.loadingLabel || "Loading..."}</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={10} sx={{ p: 3, maxWidth: 420, mx: "auto", mt: 3 }}>
      <Typography variant="h6" align="center" gutterBottom>
        {lang.twoFaSetupTitle || "Set up Two-Factor Authentication"}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }} align="center">
        {lang.twoFaSetupInstructions || "Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)"}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <img src={auth.twoFaSetupData.qrCodeDataUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
      </Box>

      <Typography variant="caption" display="block" align="center" sx={{ mb: 1 }}>
        {lang.twoFaManualEntry || "Manual entry key:"}
      </Typography>
      <Typography
        variant="body2"
        align="center"
        sx={{ fontFamily: "monospace", mb: 2, wordBreak: "break-all", bgcolor: "grey.100", p: 1, borderRadius: 1 }}
      >
        {auth.twoFaSetupData.secret}
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        {lang.twoFaBackupCodesTitle || "Backup Codes"}
      </Typography>
      <Alert severity="warning" sx={{ mb: 1 }}>
        {lang.twoFaBackupCodesWarning || "Save these codes in a safe place. Each code can only be used once."}
      </Alert>
      <Box sx={{ bgcolor: "grey.100", p: 1.5, borderRadius: 1, mb: 2, position: "relative" }}>
        <Grid container spacing={0.5}>
          {auth.twoFaSetupData.backupCodes.map((code: string, i: number) => (
            <Grid item xs={6} key={i}>
              <Typography variant="body2" align="center" sx={{ fontFamily: "monospace", fontSize: "0.95rem", letterSpacing: 1, py: 0.3 }}>
                {code}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Tooltip title={copied ? "Copied!" : "Copy all"}>
          <IconButton size="small" sx={{ position: "absolute", top: 4, right: 4 }} onClick={handleCopyBackupCodes}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        fullWidth
        label={lang.twoFaEnterCode || "Enter 6-digit code"}
        value={auth.twoFaCode}
        onChange={(e) => auth.setTwoFaCode(e.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
        sx={{ mb: 2 }}
        autoFocus
      />

      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleConfirm}
        disabled={auth.twoFaCode.length !== 6}
      >
        {lang.twoFaConfirmSetup || "Confirm Setup"}
      </Button>
    </Paper>
  );
});
